'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './ImageSection.module.css';

const BG_IMAGE_DESKTOP = 'web_v2/ring-pro/landing-page/storage/desktop.png';
const BG_IMAGE_MOBILE = 'web_v2/ring-pro/landing-page/storage/mobile.png';

const ImageSection = () => {
  const t = useTranslations('ring-pro');
  const sectionRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number>(0);
  const isVisible = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible.current = entry.isIntersecting;
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const wrapper = wrapperRef.current;
    const overlay = overlayRef.current;
    if (!section || !wrapper || !overlay) return;

    const handleScroll = () => {
      if (!isVisible.current) return;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const progress =
          1 - (rect.bottom - viewportH) / (rect.height + viewportH);
        const clamped = Math.max(0, Math.min(1, progress));
        const eased = Math.min(clamped / 0.5, 1);

        const scale = 1 + eased * 0.12;
        const translateY = (eased - 0.5) * -30;
        wrapper.style.transform = `scale(${scale}) translateY(${translateY}px)`;

        const tintProgress = Math.max(0, (clamped - 0.9) / 0.1);
        overlay.style.opacity = `${tintProgress * 0.8}`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.imageWrapper} ref={wrapperRef}>
        <CustomImage
          className={styles.desktopImage}
          src={BG_IMAGE_DESKTOP}
          alt={t('ring-pro.imageSection.imageAlt')}
          fill
          sizes="100vw"
        />
        <CustomImage
          className={styles.mobileImage}
          src={BG_IMAGE_MOBILE}
          alt={t('ring-pro.imageSection.imageAlt')}
          fill
          sizes="100vw"
        />
      </div>
      <div className={styles.tintOverlay} ref={overlayRef} />
      <div className={styles.textContainer}>
        <h2 className={styles.heading}>{t('ring-pro.imageSection.heading')}</h2>
        <p className={styles.bodyCopy}>
          {t('ring-pro.imageSection.body')}
        </p>
      </div>
    </section>
  );
};

export default ImageSection;
